/**
 * Utility functions for working with URL parameters
 */

/**
 * Helper function to safely parse URLs (handles both absolute and relative URLs)
 * @param {string} url - URL to parse
 * @returns {URL|null} - Parsed URL object or null if invalid
 */
const safeParseUrl = (url) => {
  if (!url) return null;
  
  try {
    // Handle relative URLs by creating a full URL
    if (url.startsWith('/') || url.startsWith('?')) {
      if (typeof window !== 'undefined') {
        // Browser environment - use current origin
        const fullUrl = window.location.origin + url;
        return new URL(fullUrl);
      } else {
        // Node.js environment - use a dummy origin for parsing
        const fullUrl = 'https://example.com' + url;
        return new URL(fullUrl);
      }
    }
    
    return new URL(url);
  } catch (error) {
    console.warn('Error parsing URL:', error);
    return null;
  }
};

/**
 * Get specific parameters from a URL
 * @param {string} url - The URL to extract parameters from (optional, defaults to current window location)
 * @param {string|string[]} paramNames - Single parameter name or array of parameter names to extract
 * @param {Object} options - Configuration options
 * @param {boolean} options.decode - Whether to decode the parameter values (default: true)
 * @param {*} options.defaultValue - Default value to return if parameter is not found
 * @param {boolean} options.returnAll - If true, returns all parameters as an object (ignores paramNames)
 * @returns {string|Object|Array} - Single value, object with all parameters, or array of values
 * 
 * @example
 * // Get single parameter
 * const id = getUrlParams('https://example.com?id=123&name=test', 'id');
 * // Returns: '123'
 * 
 * @example
 * // Get multiple parameters
 * const params = getUrlParams('https://example.com?id=123&name=test&active=true', ['id', 'name']);
 * // Returns: { id: '123', name: 'test' }
 * 
 * @example
 * // Get all parameters
 * const allParams = getUrlParams('https://example.com?id=123&name=test', null, { returnAll: true });
 * // Returns: { id: '123', name: 'test' }
 * 
 * @example
 * // With default value
 * const id = getUrlParams('https://example.com', 'id', { defaultValue: 'default-id' });
 * // Returns: 'default-id'
 */
export const getUrlParams = (url, paramNames, options = {}) => {
  const {
    decode = true,
    defaultValue = null,
    returnAll = false
  } = options;

  // Use current window location if no URL provided
  const targetUrl = url || (typeof window !== 'undefined' ? window.location.href : '');
  
  if (!targetUrl) {
    return returnAll ? {} : (Array.isArray(paramNames) ? {} : defaultValue);
  }

  const urlObj = safeParseUrl(targetUrl);
  if (!urlObj) {
    return returnAll ? {} : (Array.isArray(paramNames) ? {} : defaultValue);
  }
  
  const searchParams = urlObj.searchParams;

  // Return all parameters as an object
  if (returnAll) {
    const allParams = {};
    for (const [key, value] of searchParams.entries()) {
      allParams[key] = decode ? decodeURIComponent(value) : value;
    }
    return allParams;
  }

  // Handle single parameter name
  if (typeof paramNames === 'string') {
    const value = searchParams.get(paramNames);
    if (value === null) {
      return defaultValue;
    }
    return decode ? decodeURIComponent(value) : value;
  }

  // Handle array of parameter names
  if (Array.isArray(paramNames)) {
    const result = {};
    paramNames.forEach(paramName => {
      const value = searchParams.get(paramName);
      result[paramName] = value === null ? defaultValue : (decode ? decodeURIComponent(value) : value);
    });
    return result;
  }

  return defaultValue;
};

/**
 * Get URL parameters from React Router location object
 * @param {Object} location - React Router location object
 * @param {string|string[]} paramNames - Single parameter name or array of parameter names to extract
 * @param {Object} options - Configuration options (same as getUrlParams)
 * @returns {string|Object|Array} - Single value, object with all parameters, or array of values
 * 
 * @example
 * // In a React component
 * const location = useLocation();
 * const id = getUrlParamsFromLocation(location, 'id');
 * const params = getUrlParamsFromLocation(location, ['id', 'name']);
 */
export const getUrlParamsFromLocation = (location, paramNames, options = {}) => {
  if (!location || !location.search) {
    const { returnAll, defaultValue } = options;
    return returnAll ? {} : (Array.isArray(paramNames) ? {} : defaultValue);
  }

  return getUrlParams(location.search, paramNames, options);
};

/**
 * Set URL parameters without page reload
 * @param {Object} params - Object of parameters to set
 * @param {Object} options - Configuration options
 * @param {boolean} options.replace - Whether to replace current history entry (default: false)
 * @param {boolean} options.merge - Whether to merge with existing parameters (default: true)
 * @param {boolean} options.encode - Whether to encode parameter values (default: true)
 * 
 * @example
 * // Set single parameter
 * setUrlParams({ id: '123' });
 * 
 * @example
 * // Set multiple parameters
 * setUrlParams({ id: '123', name: 'test' });
 * 
 * @example
 * // Replace current history entry
 * setUrlParams({ id: '123' }, { replace: true });
 */
export const setUrlParams = (params, options = {}) => {
  const {
    replace = false,
    merge = true,
    encode = true
  } = options;

  if (typeof window === 'undefined') {
    console.warn('setUrlParams can only be used in browser environment');
    return;
  }

  try {
    const url = new URL(window.location.href);
    const searchParams = url.searchParams;

    // Clear existing parameters if not merging
    if (!merge) {
      searchParams.forEach((_, key) => searchParams.delete(key));
    }

    // Set new parameters
    Object.entries(params).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        const stringValue = String(value);
        searchParams.set(key, encode ? encodeURIComponent(stringValue) : stringValue);
      } else {
        searchParams.delete(key);
      }
    });

    // Update URL
    const newUrl = url.toString();
    if (replace) {
      window.history.replaceState({}, '', newUrl);
    } else {
      window.history.pushState({}, '', newUrl);
    }
  } catch (error) {
    console.warn('Error setting URL parameters:', error);
  }
};

/**
 * Remove specific URL parameters
 * @param {string|string[]} paramNames - Single parameter name or array of parameter names to remove
 * @param {Object} options - Configuration options
 * @param {boolean} options.replace - Whether to replace current history entry (default: false)
 * 
 * @example
 * // Remove single parameter
 * removeUrlParams('id');
 * 
 * @example
 * // Remove multiple parameters
 * removeUrlParams(['id', 'name']);
 */
export const removeUrlParams = (paramNames, options = {}) => {
  const { replace = false } = options;

  if (typeof window === 'undefined') {
    console.warn('removeUrlParams can only be used in browser environment');
    return;
  }

  try {
    const url = new URL(window.location.href);
    const searchParams = url.searchParams;

    const paramsToRemove = Array.isArray(paramNames) ? paramNames : [paramNames];
    paramsToRemove.forEach(paramName => {
      searchParams.delete(paramName);
    });

    const newUrl = url.toString();
    if (replace) {
      window.history.replaceState({}, '', newUrl);
    } else {
      window.history.pushState({}, '', newUrl);
    }
  } catch (error) {
    console.warn('Error removing URL parameters:', error);
  }
};

/**
 * Check if a URL parameter exists
 * @param {string} paramName - Parameter name to check
 * @param {string} url - The URL to check (optional, defaults to current window location)
 * @returns {boolean} - True if parameter exists, false otherwise
 * 
 * @example
 * // Check if parameter exists
 * const hasId = hasUrlParam('id', 'https://example.com?id=123');
 * // Returns: true
 */
export const hasUrlParam = (paramName, url) => {
  const targetUrl = url || (typeof window !== 'undefined' ? window.location.href : '');
  
  if (!targetUrl) {
    return false;
  }

  const urlObj = safeParseUrl(targetUrl);
  if (!urlObj) {
    return false;
  }
  
  return urlObj.searchParams.has(paramName);
};

/**
 * Get URL parameters as a query string
 * @param {Object} params - Object of parameters
 * @param {Object} options - Configuration options
 * @param {boolean} options.encode - Whether to encode parameter values (default: true)
 * @param {boolean} options.includeQuestionMark - Whether to include '?' prefix (default: true)
 * @returns {string} - Query string
 * 
 * @example
 * // Create query string
 * const queryString = createQueryString({ id: '123', name: 'test' });
 * // Returns: '?id=123&name=test'
 */
export const createQueryString = (params, options = {}) => {
  const {
    encode = true,
    includeQuestionMark = true
  } = options;

  const searchParams = new URLSearchParams();
  
  Object.entries(params).forEach(([key, value]) => {
    if (value !== null && value !== undefined) {
      const stringValue = String(value);
      searchParams.set(key, encode ? encodeURIComponent(stringValue) : stringValue);
    }
  });

  const queryString = searchParams.toString();
  return queryString ? (includeQuestionMark ? '?' + queryString : queryString) : '';
};
